import React, { useState } from 'react';
import { Formik, Form as FormikForm, Field } from 'formik';
import { Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import { FaFileExcel } from 'react-icons/fa';
import { toast } from 'react-toastify';
import moment from 'moment';
import TabelaDinamica from '~/Components/TabelaDinamica/Tabela';
import MoneyField from '~/Components/Form/MoneyField';
import {
  consultarRetornos,
  exportarDiferencaJurosExcel,
} from '~/Services/Service';
import { consultarRetornoSchema } from '../validationSchemas';

const formatarData = (v) => (v ? moment(v).format('DD/MM/YYYY') : '-');
const formatarMoeda = (v) =>
  v != null
    ? new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(v)
    : '-';

const colunasRetornos = [
  {
    title: 'Data Ocor.',
    key: 'data_ocor',
    ordenacao: true,
    align: 'center',
    width: '110px',
    render: (v) => formatarData(v),
  },
  { title: 'Cód. Título', key: 'codigo_titulo', ordenacao: true, width: '110px' },
  { title: 'Cedente', key: 'cedente', ordenacao: true },
  {
    title: 'Valor',
    key: 'valor',
    ordenacao: true,
    align: 'right',
    width: '110px',
    render: (v) => formatarMoeda(v),
  },
  {
    title: 'Juros',
    key: 'juros',
    ordenacao: true,
    align: 'right',
    width: '110px',
    render: (v) => formatarMoeda(v),
  },
  {
    title: 'Vlr Extra',
    key: 'valor_extra',
    ordenacao: true,
    align: 'right',
    width: '110px',
    render: (v) => formatarMoeda(v),
  },
  {
    title: 'Diferença',
    key: 'diferenca',
    ordenacao: true,
    align: 'right',
    width: '110px',
    render: (v) => formatarMoeda(v),
  },
  { title: 'Banco', key: 'banco', ordenacao: true, width: '120px' },
  { title: 'GE', key: 'codigo_grupo_empresa', ordenacao: true, width: '70px' },
  { title: 'Cód. Ocor.', key: 'codigo_ocor', ordenacao: true, width: '90px' },
];

const valoresIniciais = {
  codigoCliente: '',
  dataInicio: '',
  dataFim: '',
  codigoOcorrencia: '',
  limiarDiferenca: '',
};

const montarPayload = (values) => ({
  codigoCliente: Number(values.codigoCliente),
  dataInicio: values.dataInicio,
  dataFim: values.dataFim,
  codigoOcorrencia:
    values.codigoOcorrencia !== '' && values.codigoOcorrencia != null
      ? Number(values.codigoOcorrencia)
      : null,
  limiarDiferenca:
    values.limiarDiferenca !== '' && values.limiarDiferenca != null
      ? Number(values.limiarDiferenca)
      : null,
});

const TabConsultarRetornos = () => {
  const [retornos, setRetornos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingExcel, setLoadingExcel] = useState(false);
  const [filtrosAtivos, setFiltrosAtivos] = useState(null);
  const [buscaFeita, setBuscaFeita] = useState(false);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = montarPayload(values);
      const res = await consultarRetornos(payload);
      const lista = (res || []).map((item) => ({
        ...item,
        // key estável composta — pode haver múltiplas ocorrências por título (Q-07 Gate 3 A)
        id: `${item.codigo_titulo}-${item.data_ocor}-${item.codigo_ocor}`,
      }));
      setRetornos(lista);
      setFiltrosAtivos(payload);
      setBuscaFeita(true);
    } catch (err) {
      const msg =
        err && err.message ? err.message : 'Erro ao consultar retornos';
      toast.error(msg);
      setRetornos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExportarExcel = async () => {
    if (!filtrosAtivos) {
      toast.warn('Faça uma consulta antes de exportar.');
      return;
    }
    setLoadingExcel(true);
    try {
      const blob = await exportarDiferencaJurosExcel(filtrosAtivos);
      const arquivo = new Blob([blob], {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(arquivo);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `diferenca-juros-${moment().format('YYYY-MM-DD')}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg =
        err && err.message ? err.message : 'Erro ao exportar Excel';
      toast.error(msg);
    } finally {
      setLoadingExcel(false);
    }
  };

  return (
    <>
      <Formik
        initialValues={valoresIniciais}
        validationSchema={consultarRetornoSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange }) => (
          <FormikForm>
            <Row className="mb-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small mb-1">
                    Cód. Cliente Bancário
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="codigoCliente"
                    value={values.codigoCliente}
                    onChange={handleChange}
                    isInvalid={
                      touched.codigoCliente && !!errors.codigoCliente
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.codigoCliente}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small mb-1">Data início</Form.Label>
                  <Form.Control
                    type="date"
                    name="dataInicio"
                    value={values.dataInicio}
                    onChange={handleChange}
                    isInvalid={touched.dataInicio && !!errors.dataInicio}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.dataInicio}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small mb-1">Data fim</Form.Label>
                  <Form.Control
                    type="date"
                    name="dataFim"
                    value={values.dataFim}
                    onChange={handleChange}
                    isInvalid={touched.dataFim && !!errors.dataFim}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.dataFim}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small mb-1">
                    Cód. Ocorrência (opcional)
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="codigoOcorrencia"
                    value={values.codigoOcorrencia}
                    onChange={handleChange}
                    isInvalid={
                      touched.codigoOcorrencia && !!errors.codigoOcorrencia
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.codigoOcorrencia}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="small mb-1">
                    Limiar diferença (R$, opcional)
                  </Form.Label>
                  <Field
                    name="limiarDiferenca"
                    component={MoneyField}
                    className={`form-control ${
                      touched.limiarDiferenca && errors.limiarDiferenca
                        ? 'is-invalid'
                        : ''
                    }`}
                  />
                  {touched.limiarDiferenca && errors.limiarDiferenca && (
                    <div className="invalid-feedback d-block">
                      {errors.limiarDiferenca}
                    </div>
                  )}
                </Form.Group>
              </Col>
              <Col md={9} className="d-flex align-items-end" style={{ gap: '8px' }}>
                <Button type="submit" variant="primary" disabled={loading}>
                  {loading ? <Spinner animation="border" size="sm" /> : 'Buscar'}
                </Button>
                <Button
                  type="button"
                  variant="success"
                  onClick={handleExportarExcel}
                  disabled={loadingExcel || !buscaFeita || retornos.length === 0}
                >
                  {loadingExcel ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <FaFileExcel className="mr-1" /> Exportar Excel
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </FormikForm>
        )}
      </Formik>

      {buscaFeita && (
        <>
          <p className="text-muted small mt-2">
            {retornos.length === 0
              ? 'Nenhum retorno encontrado para os filtros informados.'
              : `${retornos.length} retorno(s) encontrado(s).`}
          </p>
          {retornos.length > 0 && (
            <TabelaDinamica
              colunas={colunasRetornos}
              dados={retornos}
              striped
              hover
            />
          )}
        </>
      )}
    </>
  );
};

export default TabConsultarRetornos;
